import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../../../context/AppContext';
import NovaAvatarImg from '../../../components/NovaAvatarImg';
import { fertilityDemoEditButton } from '../../../designSystem';

// NOTE: This repo is JS-only (no tsconfig). Kept file extension .jsx even though the request said .tsx.

const FC = {
  accent: '#7F77DD',
  accentDark: '#534AB7',
  accentDeep: '#3C3489',
  accentSoft: '#EEEDFE',
  accentBorder: 'rgba(127, 119, 221, 0.42)',
  gradStart: '#EEEDFE',
  gradEnd: '#E1F5EE',
  pageBg: '#FFFCF9',
  cardBg: '#FFFDF9',
  border: '#E8E0D8',
  ink: '#111111',
  body: '#555555',
  muted: '#888888',
};

const FONT_HEAD = "'Playfair Display', serif";
const FONT_BODY = "'Plus Jakarta Sans', sans-serif";

function Eyebrow({ children, style }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: FC.muted,
        fontFamily: FONT_BODY,
        marginBottom: 14,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function PillButton({ label, active, onClick, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: FONT_BODY,
        fontSize: 13,
        fontWeight: 700,
        padding: '10px 12px',
        borderRadius: 999,
        border: `0.5px solid ${active ? FC.accent : FC.border}`,
        background: active ? FC.accentSoft : FC.pageBg,
        color: active ? FC.accentDark : FC.muted,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {label}
    </button>
  );
}

function OptionButton({
  active,
  label,
  onClick,
  compact = false,
  bg = FC.pageBg,
  color = FC.muted,
  borderColor = FC.border,
  activeBg = FC.accentSoft,
  activeColor = FC.accentDark,
  children,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: FONT_BODY,
        fontSize: compact ? 12 : 12,
        fontWeight: 700,
        padding: compact ? '10px 12px' : '10px 12px',
        borderRadius: 999,
        border: `0.5px solid ${active ? FC.accent : borderColor}`,
        background: active ? activeBg : bg,
        color: active ? activeColor : color,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        textAlign: 'center',
        ...(!children
          ? {}
          : {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
            }),
      }}
    >
      {children || label}
    </button>
  );
}

function PrimaryButton({ label, onClick, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        background: FC.accent,
        color: 'white',
        border: 'none',
        borderRadius: 999,
        padding: '14px 18px',
        fontFamily: FONT_BODY,
        fontSize: 16,
        fontWeight: 700,
        cursor: 'pointer',
        ...style,
      }}
    >
      {label}
    </button>
  );
}

function NovaLink({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: FONT_BODY,
        fontSize: 12,
        fontWeight: 600,
        color: FC.accentDark,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

export default function CycleTracker({
  cycleDay = 14,
  cycleLengthAvg = 28,
  userName = 'Maya',
  navigate,
}) {
  // Some pages pass navigate down; if not, we fall back to app context navigation.
  const app = useApp();
  const nav = navigate || app?.navigate || (() => {});

  const [activeTab, setActiveTab] = useState('log');

  const STORAGE_KEY = useMemo(() => `orivonai_cycle_tracker_logged_demo_day_${cycleDay}`, [cycleDay]);
  const [loggedEntry, setLoggedEntry] = useState(null);
  const [showSavedBanner, setShowSavedBanner] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.logged) return;
      setLoggedEntry(parsed);
      setShowSavedBanner(true);
    } catch {
      // ignore
    }
  }, [STORAGE_KEY]);

  // ===== Nova strip: phase aware =====
  const getNovaMessage = (d) => {
    if (d >= 1 && d <= 5)
      return `You're on day ${d} — your period should be here. Cramps and tiredness are really common right now. What do you need today?`;
    if (d >= 6 && d <= 11)
      return `You're on day ${d} — energy often picks up around now as oestrogen rises. How are you feeling compared to last week?`;
    if (d >= 12 && d <= 16)
      return `You're on day ${d} — roughly the middle of your cycle. Energy is usually highest now. How are you feeling today?`;
    if (d >= 17 && d <= 23)
      return `You're on day ${d} — you're in the second half of your cycle. Some people start noticing mood or energy shifts around now. Anything going on?`;
    if (d >= 24 && d <= 28)
      return `You're on day ${d} — you're in the pre-period window. If you're feeling lower or more irritable than usual, that's your hormones, not you. How are you doing?`;
    return `You're on day ${d} — how are you feeling today?`;
  };

  const novaMessage = getNovaMessage(cycleDay);
  const periodInDays = Math.max(0, cycleLengthAvg - cycleDay);

  // ===== Tab 1 state =====
  const moodOptions = useMemo(
    () => [
      { emoji: '😩', label: 'Rough day' },
      { emoji: '😢', label: 'Feeling sad' },
      { emoji: '😔', label: 'Low' },
      { emoji: '😐', label: 'Just okay' },
      { emoji: '🙂', label: 'Pretty good' },
      { emoji: '😊', label: 'Good' },
      { emoji: '🤩', label: 'Great' },
    ],
    []
  );

  const [moodIndex, setMoodIndex] = useState(null);
  const [symptoms, setSymptoms] = useState(new Set());
  const [energyLevel, setEnergyLevel] = useState(null);
  const [periodStatus, setPeriodStatus] = useState('Not yet');
  const [flowIntensity, setFlowIntensity] = useState(null);

  const inExpectedPeriodWindow = cycleDay >= 1 && cycleDay <= 5;
  const periodActive = periodStatus !== 'Not yet';

  const moodEmoji = moodIndex !== null ? moodOptions[moodIndex]?.emoji : null;
  const handleToggleSymptom = (sym) => {
    setSymptoms((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym);
      else next.add(sym);
      return next;
    });
  };

  const [todayLoadedOnce, setTodayLoadedOnce] = useState(false);
  useEffect(() => {
    if (todayLoadedOnce) return;
    // If we already have a demo logged entry, prefill (so "Edit" makes sense).
    if (loggedEntry?.moodEmoji) {
      const idx = moodOptions.findIndex((m) => m.emoji === loggedEntry.moodEmoji);
      if (idx >= 0) setMoodIndex(idx);
      // We keep other demo fields minimal; this is a demo page.
      setIsEditing(false);
    }
    setTodayLoadedOnce(true);
  }, [loggedEntry, moodOptions, todayLoadedOnce]);

  const onSave = () => {
    if (moodIndex === null) return;
    const payload = {
      logged: true,
      cycleDay,
      moodEmoji,
      savedAt: Date.now(),
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore
    }
    setLoggedEntry(payload);
    setShowSavedBanner(false);
    setShowSaveConfirmation(true);
    setIsEditing(false);
    setTimeout(() => setShowSaveConfirmation(false), 4500);
  };

  // ===== Tab 2 calendar data =====
  const phaseRows = useMemo(
    () => [
      {
        emoji: '🔴',
        name: 'Period',
        daysLabel: 'Days 1–5',
        body: 'This is the start of your cycle — when you bleed. Cramps and tiredness are common. Rest if you need to.',
      },
      {
        emoji: '🌸',
        name: 'Follicular',
        daysLabel: 'Days 6–11',
        body: 'Oestrogen rises. Energy and mood often lift. A good time to tackle things that take focus.',
      },
      {
        emoji: '🌙',
        name: 'Ovulation',
        daysLabel: 'Days 12–16',
        body: 'An egg is released. Energy usually peaks. You might feel more social and confident.',
      },
      {
        emoji: '💜',
        name: 'Luteal',
        daysLabel: 'Days 17–28',
        body: 'Progesterone rises then drops. PMS symptoms can appear. Rest more, expect some mood shifts.',
      },
    ],
    []
  );

  const CalendarGrid = () => {
    const year = 2026;
    const monthIndex = 2; // March
    const daysInMonth = 31;

    const firstDayDow = new Date(Date.UTC(year, monthIndex, 1)).getUTCDay(); // 0=Sun
    const totalCells = 42; // 6 weeks
    const todayDay = 23; // Mar 23

    const periodStart = 9;
    const periodEnd = 13;

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 12 }}>
        {Array.from({ length: totalCells }, (_, cell) => {
          const dayNum = cell - firstDayDow + 1;
          const isInMonth = dayNum >= 1 && dayNum <= daysInMonth;
          if (!isInMonth)
            return (
              <div key={`blank-${cell}`} style={{ height: 36 }} />
            );

          const isPeriod = dayNum >= periodStart && dayNum <= periodEnd;
          const isToday = dayNum === todayDay;

          const bg = isPeriod ? '#F4C0D1' : isToday ? FC.accentSoft : 'transparent';
          const color = isPeriod ? '#72243E' : isToday ? FC.accentDark : FC.body;
          const border = isToday ? `0.5px solid ${FC.accent}` : '0.5px solid transparent';

          return (
            <div
              key={dayNum}
              role="button"
              tabIndex={0}
              onClick={() => {}}
              style={{
                textAlign: 'center',
                borderRadius: 8,
                padding: '6px 2px',
                fontSize: 12,
                fontWeight: isToday ? 700 : 400,
                background: bg,
                color,
                border,
                cursor: 'pointer',
                position: 'relative',
                minHeight: 36,
              }}
            >
              {dayNum}
              {isToday && <div style={{ fontSize: 13, position: 'absolute', top: -2, right: 0 }}>🌙</div>}
              {isPeriod && <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#D4537E', margin: '2px auto 0' }} />}
            </div>
          );
        })}
      </div>
    );
  };

  // ===== Tab 7 Comfort plan (keep existing content unchanged) =====
  const ComfortPlanTab = () => {
    // Inline re-implementation based on current `FirstChapterMyJourney` comfort plan tab.
    // Keeping the copy and structure unchanged.
    const NovaBanner = ({ text, prompts = [] }) => (
      <div
        style={{
          background: FC.accentSoft,
          border: `0.5px solid ${FC.accentBorder}`,
          borderRadius: 14,
          padding: '12px 14px',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
          marginBottom: 14,
        }}
      >
        <NovaAvatarImg chapter="ttc" size={28} alt="" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: FC.accentDeep, lineHeight: 1.6, marginBottom: prompts.length ? 10 : 0 }}>{text}</div>
          {prompts.length > 0 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {prompts.map((p) => (
                <button
                  key={p}
                  style={{
                    fontFamily: FONT_BODY,
                    fontSize: 12,
                    padding: '5px 12px',
                    background: FC.pageBg,
                    border: `0.5px solid ${FC.accentBorder}`,
                    borderRadius: 999,
                    cursor: 'pointer',
                    color: FC.accentDeep,
                    fontWeight: 700,
                  }}
                  type="button"
                  onClick={() => nav('/nova')}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          style={{
            fontFamily: FONT_BODY,
            fontSize: 12,
            fontWeight: 600,
            color: FC.accent,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            padding: 0,
          }}
          type="button"
          onClick={() => nav('/nova')}
        >
          Chat with Nova →
        </button>
      </div>
    );

    const C = {
      primary: FC.accent,
      primaryL: FC.gradStart,
      primaryM: FC.accentBorder,
      primaryDk: FC.accentDark,
      sand: '#F9F6F1',
      sand2: '#EDE8DF',
      white: FC.pageBg,
      border: FC.border,
      muted: FC.muted,
      text: FC.ink,
      text2: FC.body,
      r: 14,
    };

    const Btn = ({ children, variant = 'primary', style = {}, onClick }) => {
      const vs = {
        primary: { background: C.primary, color: '#fff', border: 'none' },
        ghost: { background: C.primaryL, color: C.primaryDk, border: `0.5px solid ${C.primaryM}` },
        sand: { background: C.sand, color: C.muted, border: `1px solid ${C.border}` },
      };
      return (
        <button
          onClick={onClick}
          type="button"
          style={{
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 700,
            borderRadius: 999,
            padding: '7px 16px',
            cursor: 'pointer',
            ...vs[variant],
            ...style,
          }}
        >
          {children}
        </button>
      );
    };

    return (
      <div style={{ fontFamily: FONT_BODY }}>
        <div style={{ padding: '0 0 24px' }}>
          <NovaBanner
            text="Your comfort plan is just for you. It's the list of things that actually help when your period or cramps are bad. Nova helped build this one — add what works for you."
            prompts={['What helps with bad cramps?', 'Is heat always safe?', 'How much ibuprofen can I take?']}
          />

          {[
            { icon: '🔥', title: 'Heat', body: 'A hot water bottle or heat pad on your lower tummy or lower back is one of the most effective non-medication options. Use a cover — never put it directly on skin.' },
            { icon: '💊', title: 'Pain relief', body: 'Ibuprofen (like Advil) works better than paracetamol for period cramps because it reduces the prostaglandins that cause them. Take with food and follow the packet. Check with a parent first if you&apos;re unsure.' },
            { icon: '🚶', title: 'Gentle movement', body: 'Light walking or stretching can actually help cramps by increasing blood flow. You don&apos;t have to push through pain — but 10 minutes of slow movement sometimes genuinely helps.' },
            { icon: '😴', title: 'Rest', body: 'Period fatigue is real — progesterone dips and iron loss from bleeding can make you tired. Rest when you need to, especially on days 1–2.' },
            { icon: '🥣', title: 'Food & drink', body: 'Staying hydrated helps with bloating and headaches. Salty and sugary foods can make bloating worse. Magnesium-rich foods (dark chocolate, bananas, nuts) may help with cramps.' },
            { icon: '💆', title: 'Distraction & support', body: 'Sometimes the best comfort is talking to someone, watching something, or just not being alone with it. This community is here.' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '12px 0', borderBottom: `1px solid ${C.sand2}` }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>{item.icon}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>{item.title}</div>
                <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.55 }}>{item.body}</div>
              </div>
            </div>
          ))}

          <Btn variant="primary" style={{ width: '100%', marginTop: 14 }}>
            Save my comfort plan
          </Btn>
        </div>
      </div>
    );
  };

  // ===== Tab content =====
  const symptomsOptions = useMemo(
    () => ['Cramps', 'Headache', 'Bloating', 'Tired', 'Tender/sore', 'Heavy feeling', 'Spotting', 'Feeling great actually'],
    []
  );
  const energyOptions = useMemo(
    () => [
      { id: 'drained', label: '🪫 Drained' },
      { id: 'low', label: '😴 Low' },
      { id: 'okay', label: '😐 Okay' },
      { id: 'good', label: '⚡ Good' },
      { id: 'high', label: '🚀 High' },
    ],
    []
  );
  const flowOptions = useMemo(
    () => [
      { id: 'light', label: 'Light' },
      { id: 'moderate', label: 'Moderate' },
      { id: 'heavy', label: 'Heavy' },
      { id: 'veryheavy', label: 'Very heavy' },
    ],
    []
  );

  const Tab1LogToday = () => {
    const periodLabelDay = `Day ${cycleDay} of period`;

    return (
      <div style={{ marginTop: 8 }}>
        <Eyebrow>HOW ARE YOU FEELING TODAY?</Eyebrow>
        <div style={{ fontSize: 13, color: FC.body, lineHeight: 1.7, marginBottom: 18 }}>
          Tracking your mood alongside your cycle helps you spot patterns — like if you always feel low a few days before your period. Totally normal if you do.
        </div>

        {showSavedBanner && loggedEntry && (
          <div
            style={{
              background: FC.gradEnd,
              border: `1px solid #9FE1CB`,
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: FC.accentDark, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>Logged today ✓</span>
              {loggedEntry.moodEmoji ? <span style={{ fontSize: 16 }}>{loggedEntry.moodEmoji}</span> : null}
            </div>
            <button
              type="button"
              onClick={() => {
                setIsEditing(true);
                setShowSavedBanner(false);
              }}
              style={{
                fontFamily: FONT_BODY,
                whiteSpace: 'nowrap',
                ...fertilityDemoEditButton,
                fontWeight: 600,
              }}
            >
              Edit today&apos;s entry
            </button>
          </div>
        )}

        {showSaveConfirmation && (
          <div
            style={{
              background: FC.gradEnd,
              borderRadius: 12,
              padding: '12px 14px',
              marginBottom: 16,
              border: `1px solid rgba(159, 225, 203, 0.9)`,
              fontSize: 13,
              fontWeight: 700,
              color: FC.accentDark,
            }}
          >
            Logged. Nova will look for patterns as you keep tracking.
          </div>
        )}

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: FC.muted, marginBottom: 10 }}>Pick the emoji that fits best</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 4 }}>
            {moodOptions.map((m, idx) => {
              const active = moodIndex === idx;
              return (
                <button
                  key={m.emoji}
                  type="button"
                  onClick={() => setMoodIndex(idx)}
                  style={{
                    flex: '0 0 auto',
                    borderRadius: 16,
                    border: `0.5px solid ${active ? FC.accent : FC.border}`,
                    background: active ? FC.accentSoft : FC.pageBg,
                    cursor: 'pointer',
                    padding: '12px 10px 10px',
                    width: 92,
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 22, lineHeight: 1.1 }}>{m.emoji}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, marginTop: 8, color: active ? FC.accentDark : FC.muted }}>{m.label}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: FC.ink, marginBottom: 10 }}>Anything going on with your body?</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {symptomsOptions.map((s) => {
              const active = symptoms.has(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleToggleSymptom(s)}
                  style={{
                    fontFamily: FONT_BODY,
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '8px 14px',
                    borderRadius: 999,
                    border: `0.5px solid ${active ? FC.accent : FC.border}`,
                    background: active ? FC.accentSoft : FC.pageBg,
                    color: active ? FC.accentDark : FC.muted,
                    cursor: 'pointer',
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: FC.ink, marginBottom: 10 }}>Energy today?</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {energyOptions.map((o) => {
              const active = energyLevel === o.id;
              return (
                <OptionButton
                  key={o.id}
                  active={active}
                  label={o.label}
                  onClick={() => setEnergyLevel(o.id)}
                  bg={FC.pageBg}
                  color={FC.muted}
                  borderColor={FC.border}
                />
              );
            })}
          </div>
        </div>

        {inExpectedPeriodWindow && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: FC.ink, marginBottom: 10 }}>Is your period here?</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <OptionButton active={periodStatus === 'Not yet'} label="Not yet" onClick={() => setPeriodStatus('Not yet')} bg={FC.pageBg} color={FC.muted} borderColor={FC.border} />
              <OptionButton
                active={periodStatus === 'Just started'}
                label="Just started"
                onClick={() => setPeriodStatus('Just started')}
                bg={FC.pageBg}
                color={FC.muted}
                borderColor={FC.border}
              />
              <OptionButton
                active={periodStatus === periodLabelDay}
                label={periodLabelDay}
                onClick={() => setPeriodStatus(periodLabelDay)}
                bg={FC.pageBg}
                color={FC.muted}
                borderColor={FC.border}
              />
            </div>
            {periodStatus === 'Just started' && (
              <div style={{ marginTop: 12, background: FC.accentSoft, border: `0.5px solid ${FC.accentBorder}`, borderRadius: 12, padding: '12px 14px', color: FC.accentDark, fontSize: 13, fontWeight: 700, lineHeight: 1.6 }}>
                Nova has noted this as the start of a new cycle.
              </div>
            )}
          </div>
        )}

        {periodActive && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: FC.ink, marginBottom: 10 }}>How&apos;s your flow?</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {flowOptions.map((o) => {
                const active = flowIntensity === o.id;
                return (
                  <OptionButton
                    key={o.id}
                    active={active}
                    label={o.label}
                    onClick={() => setFlowIntensity(o.id)}
                    bg={FC.pageBg}
                    color={FC.muted}
                    borderColor={FC.border}
                  />
                );
              })}
            </div>
          </div>
        )}

        <PrimaryButton label="Save today ✓" onClick={onSave} />
      </div>
    );
  };

  const Tab2Calendar = () => {
    return (
      <div>
        <div
          style={{
            background: FC.pageBg,
            border: `0.5px solid ${FC.border}`,
            borderRadius: 14,
            padding: 16,
          }}
        >
          <Eyebrow style={{ marginBottom: 12, color: FC.muted }}>YOUR CYCLE CALENDAR — MARCH</Eyebrow>
          <div style={{ fontSize: 13, color: FC.muted, marginBottom: 18, lineHeight: 1.7 }}>
            The pink days show your last period. The moon shows today. Tap any day to add how you felt.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 12 }}>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
              <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: FC.muted, paddingBottom: 4 }}>
                {d}
              </div>
            ))}
          </div>

          <CalendarGrid />

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: FC.muted }}>
              <span style={{ color: '#D4537E', fontWeight: 700 }}>●</span>
              <span>Period days (Mar 9–13)</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: FC.muted }}>
              <span style={{ color: FC.accentDark, fontWeight: 700 }}>●</span>
              <span>Today (day 14)</span>
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <Eyebrow style={{ marginBottom: 12, color: FC.muted }}>WHAT THE PHASES MEAN</Eyebrow>
            {phaseRows.map((p) => (
              <div key={p.name} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: `0.5px solid ${FC.border}` }}>
                <div style={{ fontSize: 22, flexShrink: 0 }}>{p.emoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '2px 10px',
                        borderRadius: 999,
                        background: '#F3F1FE',
                        color: FC.accentDark,
                        border: `0.5px solid ${FC.accentBorder}`,
                      }}
                    >
                      {p.daysLabel}
                    </span>
                    <div style={{ fontSize: 13, fontWeight: 800, color: FC.ink }}>{p.name}</div>
                  </div>
                  <div style={{ fontSize: 13, color: FC.body, lineHeight: 1.55 }}>{p.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const Tab3CycleHistory = () => {
    const cards = [
      { label: 'Avg cycle length', value: '28 days', sub: 'Regular', color: FC.accent },
      { label: 'Avg period length', value: '5 days', sub: 'Normal range', color: FC.accent },
      { label: 'Mood patterns', value: 'Tracked', sub: '3 days logged', color: FC.ink },
    ];
    const entries = [
      { month: 'Mar 2026', duration: '5 days', start: 'Started Mar 9', flow: 'Moderate flow', cramps: 'Mild cramps' },
      { month: 'Feb 2026', duration: '5 days', start: 'Started Feb 9', flow: 'Moderate flow', cramps: 'Moderate cramps' },
      { month: 'Jan 2026', duration: '6 days', start: 'Started Jan 12', flow: 'Moderate flow', cramps: 'Mild cramps' },
    ];
    return (
      <div>
        <div style={{ marginBottom: 14 }}>
          <Eyebrow>YOUR CYCLE HISTORY</Eyebrow>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
            {cards.map((c) => (
              <div key={c.label} style={{ background: '#F9F6F1', borderRadius: 14, padding: '12px 14px' }}>
                <div style={{ fontSize: 10, color: FC.muted, marginBottom: 4 }}>{c.label}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.value}</div>
                <div style={{ fontSize: 10, color: FC.muted, marginTop: 2 }}>{c.sub}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ border: `0.5px solid ${FC.border}`, borderRadius: 14, background: FC.pageBg, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: FC.muted, fontWeight: 700, marginBottom: 8 }}>History</div>
          {entries.map((e, idx) => (
            <div key={idx} style={{ padding: '10px 0', borderBottom: idx < entries.length - 1 ? `0.5px solid ${FC.border}` : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontFamily: FONT_HEAD, fontSize: 16, fontWeight: 700, color: FC.ink }}>{e.month}</div>
                <div
                  style={{
                    background: FC.accentSoft,
                    border: `0.5px solid ${FC.accentBorder}`,
                    color: FC.accentDark,
                    borderRadius: 999,
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                  }}
                >
                  [{e.duration}]
                </div>
              </div>
              <div style={{ fontSize: 13, color: FC.body }}>
                {e.start} · {e.flow} · {e.cramps}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            background: FC.gradEnd,
            border: `0.5px solid #9FE1CB`,
            borderRadius: 12,
            padding: '12px 14px',
            color: FC.accentDark,
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.6,
          }}
        >
          The more cycles you log, the better Nova can spot patterns — and the more useful this history becomes at a doctor&apos;s appointment.
        </div>
      </div>
    );
  };

  const Tab4Normalcy = () => {
    const rows = [
      { label: 'Cycle length', value: '28 days', status: { kind: 'normal', text: '✓ Normal (21–35 days is the range)' } },
      { label: 'Period length', value: '5 days', status: { kind: 'normal', text: '✓ Normal (2–7 days is the range)' } },
      { label: 'Flow', value: 'Moderate', status: { kind: 'normal', text: '✓ Normal' } },
      { label: 'Cramps', value: 'Mild-moderate', status: { kind: 'normal', text: '✓ Normal (but you&apos;re allowed to treat them!)' } },
      { label: 'Mood pattern', value: 'Some pre-period dips', status: { kind: 'common', text: '✓ Common (PMS)' } },
    ];
    const badgeStyle = (kind) => {
      if (kind === 'normal') return { bg: FC.gradEnd, color: '#085041' };
      if (kind === 'common') return { bg: FC.accentSoft, color: FC.accentDark };
      return { bg: '#FEF0EB', color: '#AD3F1A' };
    };

    const suggested = ['Is my cycle normal?', 'When should I first see a gynecologist?', 'What&apos;s a light vs heavy period?'];

    return (
      <div>
        <Eyebrow>ORA&apos;S NORMALCY CHECK</Eyebrow>
        <div
          style={{
            background: FC.accentSoft,
            border: `0.5px solid rgba(127,119,221,.4)`,
            borderRadius: 12,
            padding: '14px 16px',
            marginBottom: 14,
            color: FC.body,
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.55,
          }}
        >
          Based on your last 3 cycles: your cycle length is regular, your flow is in the normal range, and your cramps are mild to moderate. You&apos;re doing great at tracking — keep going and Nova will spot any patterns worth noting.
        </div>

        <div style={{ border: `0.5px solid ${FC.border}`, borderRadius: 14, overflow: 'hidden' }}>
          {rows.map((r, idx) => {
            const alt = idx % 2 === 1;
            const b = badgeStyle(r.status.kind);
            return (
              <div
                key={r.label}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 240px',
                  gap: 12,
                  padding: '12px 14px',
                  background: alt ? '#FAFAFA' : FC.pageBg,
                  alignItems: 'center',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: FC.body }}>{r.label}</div>
                <div style={{ fontSize: 13, color: FC.body }}>{r.value}</div>
                <div style={{ justifySelf: 'end' }}>
                  <span
                    style={{
                      background: b.bg,
                      color: b.color,
                      borderRadius: 999,
                      padding: '6px 12px',
                      fontSize: 12,
                      fontWeight: 800,
                      border: `0.5px solid rgba(0,0,0,0.02)`,
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                      textAlign: 'center',
                    }}
                  >
                    {r.status.text}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 14, background: FC.pageBg, border: `0.5px solid ${FC.border}`, borderRadius: 14, padding: 14 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {suggested.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => nav('/nova')}
                style={{
                  fontFamily: FONT_BODY,
                  fontSize: 12,
                  fontWeight: 700,
                  padding: '8px 12px',
                  borderRadius: 999,
                  border: `0.5px solid ${FC.border}`,
                  background: FC.pageBg,
                  color: FC.accentDark,
                  cursor: 'pointer',
                }}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const Tab5WhenToSeeDoctor = () => {
    const symptomRows = [
      { text: 'Cramps so bad you miss school or activities', tag: { label: 'Pain', bg: '#FCEBEB', color: '#A32D2D' } },
      { text: 'Bleeding that soaks through a pad or tampon every hour for several hours', tag: { label: 'Heavy flow', bg: '#FEF0EB', color: '#AD3F1A' } },
      { text: 'Periods that are very irregular or stop completely', tag: { label: 'Irregular', bg: FC.accentSoft, color: FC.accentDark } },
      { text: 'Severe mood changes that affect your daily life', tag: { label: 'Mood', bg: FC.gradEnd, color: '#085041' } },
      { text: 'Any symptoms that worry you — trust your gut', tag: { label: 'General', bg: '#F9F6F1', color: '#444441' } },
      { text: 'No period within 3 years of starting puberty, or by age 15', tag: { label: 'Irregular', bg: FC.accentSoft, color: FC.accentDark } },
      { text: 'Period pain that gets worse over time rather than better', tag: { label: 'Pain', bg: '#FCEBEB', color: '#A32D2D' } },
    ];

    return (
      <div>
        <Eyebrow>WHEN TO SEE A DOCTOR</Eyebrow>
        <div style={{ fontSize: 13, color: FC.body, lineHeight: 1.7, marginBottom: 16 }}>
          You don&apos;t need a specific reason to see a gynecologist — you can just go and say you want to understand your cycle. Here are signs it&apos;s worth booking:
        </div>

        <div style={{ border: `0.5px solid ${FC.border}`, borderRadius: 14, overflow: 'hidden', marginBottom: 16, background: FC.pageBg }}>
          {symptomRows.map((r, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 14,
                padding: '12px 14px',
                background: idx % 2 === 0 ? FC.pageBg : '#FAFAFA',
                borderBottom: idx < symptomRows.length - 1 ? `0.5px solid ${FC.border}` : 'none',
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: FC.body, fontWeight: 700 }}>
                <span style={{ color: FC.accentDark, fontSize: 16, lineHeight: '16px', marginTop: -2 }}>•</span>
                <span style={{ lineHeight: 1.5 }}>{r.text}</span>
              </div>
              <span
                style={{
                  background: r.tag.bg,
                  color: r.tag.color,
                  borderRadius: 3,
                  padding: '7px 10px',
                  fontSize: 12,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                }}
              >
                {r.tag.label}
              </span>
            </div>
          ))}
        </div>

        {/* Tag labels aren't specified in the prompt; keep empty pill borderless label. */}
        <PrimaryButton
          label="Find a doctor near me →"
          onClick={() => nav('/doctors')}
          style={{ marginBottom: 10 }}
        />

        <div style={{ fontSize: 13, color: FC.muted, lineHeight: 1.6 }}>
          You can also just go to your GP and say: &quot;I&apos;ve just started my period and I want to understand what&apos;s normal.&quot; No specific problem needed.
        </div>
      </div>
    );
  };

  const Tab6Patterns = () => {
    const cards = [
      {
        title: 'Pre-period mood dip',
        body: "You've logged lower mood on days 24–27 of your last 2 cycles. That's the late luteal phase — totally normal and tied to progesterone dropping. Knowing it's coming makes it easier to handle.",
        tip: 'Try logging your mood every day for one more cycle. If the pattern holds, that\'s useful information to have — for yourself and if you ever speak to a doctor.',
      },
      {
        title: 'Cramps on day 1–2',
        body: 'Your cramps have been mild-moderate on days 1 and 2 of your last 3 cycles, then easing off. That’s the typical pattern — uterine contractions are highest when bleeding starts.',
        tip: 'Ibuprofen works best if you take it before cramps peak — try taking it as soon as your period starts, not after the pain is already bad.',
      },
      {
        title: 'Regular cycle — 27–28 days',
        body: 'Your last 3 cycles have been 28, 27, and 28 days. That’s very regular for someone who’s been tracking for a few months. Irregularity is common early on — yours is settling into a reliable pattern.',
        tip: 'Next predicted period: around Apr 6. Nova will remind you 3 days before.',
      },
    ];

    const TipBox = ({ children }) => (
      <div style={{ background: FC.gradEnd, border: '1px solid #9FE1CB', borderRadius: 12, padding: '12px 14px', color: '#085041', fontSize: 13, fontWeight: 800, lineHeight: 1.55 }}>
        {children}
      </div>
    );

    return (
      <div style={{ display: 'grid', gap: 12 }}>
        {cards.map((c) => (
          <div key={c.title} style={{ border: `0.5px solid ${FC.border}`, borderRadius: 14, background: FC.pageBg, padding: 16 }}>
            <div style={{ fontFamily: FONT_HEAD, fontSize: 16, fontWeight: 800, color: FC.ink, marginBottom: 8 }}>{c.title}</div>
            <div style={{ fontSize: 13, color: FC.body, lineHeight: 1.7, marginBottom: 12 }}>{c.body}</div>
            <TipBox>{c.tip}</TipBox>
          </div>
        ))}
      </div>
    );
  };

  const statsRow = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
      <div style={{ padding: 2 }}>
        <div style={{ fontSize: 13, color: FC.muted, fontWeight: 700 }}>Cycle day</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: FC.ink, marginTop: 2 }}>14</div>
        <div style={{ fontSize: 13, color: FC.body, fontWeight: 700, marginTop: 2 }}>Middle of your cycle</div>
      </div>
      <div style={{ padding: 2 }}>
        <div style={{ fontSize: 13, color: FC.muted, fontWeight: 700 }}>Cycle length</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: FC.ink, marginTop: 2 }}>28 days</div>
        <div style={{ fontSize: 13, color: FC.body, fontWeight: 700, marginTop: 2 }}>Pretty regular!</div>
      </div>
      <div style={{ padding: 2 }}>
        <div style={{ fontSize: 13, color: FC.muted, fontWeight: 700 }}>Mood streak</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: FC.ink, marginTop: 2 }}>3 days</div>
        <div style={{ fontSize: 13, color: FC.body, fontWeight: 700, marginTop: 2 }}>Logging every day</div>
      </div>
    </div>
  );

  const streakBadges = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
      {[
        { icon: '🔥', text: '3-day logging streak' },
        { icon: '📅', text: '3 cycles tracked' },
        { icon: '⭐', text: 'Regular pattern' },
      ].map((b) => (
        <div
          key={b.text}
          style={{
            background: '#F9F6F1',
            border: `0.5px solid ${FC.border}`,
            borderRadius: 999,
            padding: '9px 12px',
            fontSize: 11,
            fontWeight: 700,
            color: FC.body,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 13 }}>{b.icon}</span>
          <span>{b.text}</span>
        </div>
      ))}
    </div>
  );

  const StatusCard = () => {
    return (
      <div
        style={{
          background: `linear-gradient(140deg, ${FC.gradStart}, ${FC.gradEnd})`,
          borderRadius: 18,
          border: `1px solid ${FC.accentBorder}`,
          padding: '18px 20px',
          boxShadow: '0 2px 16px rgba(17, 17, 17, 0.06)',
        }}
      >
        <div style={{ fontFamily: FONT_BODY, fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: FC.accentDark, marginBottom: 8 }}>
          FIRST CHAPTER · CYCLE DAY 14
        </div>
        <div style={{ fontFamily: FONT_HEAD, fontSize: 22, fontWeight: 800, lineHeight: 1.25, color: FC.ink, marginBottom: 6 }}>
          {userName}, day {cycleDay} of your cycle. 🌙
        </div>
        <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: FC.muted, lineHeight: 1.6, marginBottom: 14 }}>
          Last period: Mar 9 · Cycle length: ~28 days · Next period: ~Apr 6
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              padding: '4px 12px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.7)',
              color: FC.accentDark,
              border: `0.5px solid ${FC.accentSoft}`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: FC.accent, display: 'inline-block' }} />
            Cycle day {cycleDay}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              padding: '4px 12px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.7)',
              color: FC.accentDark,
              border: `0.5px solid ${FC.accentBorder}`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            3-day mood streak
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              padding: '4px 12px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.7)',
              color: FC.accentDark,
              border: `0.5px solid ${FC.accentBorder}`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            Regular cycle
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              padding: '4px 12px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.7)',
              border: `0.5px solid ${FC.accentBorder}`,
              color: FC.accentDark,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            Period in ~{periodInDays} days
          </span>
        </div>
      </div>
    );
  };

  const NovaStrip = () => (
    <div
      style={{
        background: FC.cardBg,
        border: `1px solid ${FC.border}`,
        borderRadius: 14,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxShadow: '0 2px 16px rgba(17, 17, 17, 0.04)',
      }}
    >
      <NovaAvatarImg chapter="ttc" size={28} alt="" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: FC.accentDark, marginBottom: 3, fontFamily: FONT_BODY }}>Nova · hey 👋</div>
        <div style={{ fontSize: 13, color: FC.ink, lineHeight: 1.6 }}>{novaMessage}</div>
      </div>
      <NovaLink onClick={() => nav('/nova')}>Open Nova →</NovaLink>
    </div>
  );

  return (
    <div
      style={{
        background: 'transparent',
        fontFamily: FONT_BODY,
        paddingBottom: 30,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <StatusCard />
      <NovaStrip />
      <div
        style={{
        background: FC.cardBg,
        border: `0.5px solid ${FC.border}`,
        borderRadius: 16,
        padding: '14px 16px',
        boxShadow: '0 2px 16px rgba(17, 17, 17, 0.04)',
      }}
    >
      {statsRow}
      {streakBadges}
    </div>

      <div
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: `1px solid ${FC.border}`,
          overflowX: 'auto',
          background: FC.cardBg,
          borderRadius: 16,
          padding: '0 4px',
          border: `0.5px solid ${FC.border}`,
          boxShadow: '0 2px 16px rgba(17, 17, 17, 0.04)',
        }}
      >
        {[
          ['log', 'Log today'],
          ['calendar', 'My calendar'],
          ['history', 'Cycle history'],
          ['normal', 'Am I normal?'],
          ['doctor', 'When to see a doctor'],
          ['patterns', 'Patterns'],
          ['comfort', 'Comfort plan'],
        ].map(([key, label]) => {
          const active = activeTab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              style={{
                flex: '0 0 auto',
                fontFamily: FONT_BODY,
                fontSize: 13,
                fontWeight: active ? 700 : 700,
                color: active ? FC.accentDark : FC.muted,
                background: 'none',
                border: 'none',
                borderBottom: active ? `3px solid ${FC.accent}` : '3px solid transparent',
                padding: '12px 14px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div
        style={{
          padding: '16px 18px 20px',
          background: FC.cardBg,
          border: `0.5px solid ${FC.border}`,
          borderRadius: 16,
          boxShadow: '0 2px 16px rgba(17, 17, 17, 0.04)',
        }}
      >
        {activeTab === 'log' && <Tab1LogToday />}
        {activeTab === 'calendar' && <Tab2Calendar />}
        {activeTab === 'history' && <Tab3CycleHistory />}
        {activeTab === 'normal' && <Tab4Normalcy />}
        {activeTab === 'doctor' && <Tab5WhenToSeeDoctor />}
        {activeTab === 'patterns' && <Tab6Patterns />}
        {activeTab === 'comfort' && <ComfortPlanTab />}
      </div>
    </div>
  );
}

